/**
 * v5: v4's composition, v2's performance.
 *
 * The melody, harmony, form and theme memory are v4's (same streams: a seed's
 * theme is identical in v4 and v5, and genre/meter/Adventure work the same).
 * What changes is everything inside the bar, taken from v2 but made meter-aware:
 *
 * 1. v2 MIX + FX: bass-forward, quiet hats, full drive, open filter, deep duck.
 * 2. ENERGY RAMPS: the last bars of a section rise (or fall) into the next;
 *    brightness and velocity follow per bar, and quiet sections close the
 *    master filter, then reopen into the return.
 * 3. EUCLIDEAN HATS/PERC on the bar's real 16th grid (14 steps in 7/8, 12 in
 *    6/8, 20 in 5/4), rotated per phrase, accents on the beat groups.
 * 4. LEAD: a selectable voice (default v2's pulse); short glides only on small
 *    steps into held notes; a quiet octave doubling on the return. (No falls or
 *    low-octave theme: with v4's shorter phrases they read as goofy.)
 * 5. TIERED LAYERS: arp (odd length, phasing against the bar), chopped pulse
 *    chords in city/flight, bells; one layer rests for a phrase in long sections.
 * 6. TRANSITIONS: dropped beat into big sections, crash on their downbeat,
 *    risers, half-beat stop-time, fills on the bar's last beat group.
 * 7. RECALL WITH VARIATION: a pickup note into the returning theme and a bell
 *    counter-line in its gaps; the notes of the theme stay intact.
 * 8. COMPING + BASS by energy: keys skip/anticipate/invert per phrase; bass goes
 *    held → kick-locked → Euclidean syncopation as energy rises.
 */
import { KEYS, MODES, NOUNS, PALETTES, ARCHETYPES, Rng, Scale, type FxParams, type Patch, type InstId, type SNote, type SynthRequest, type SynthScore } from './compose';
import { euclid } from './compose2';
import { makeV2Patches } from './instruments2';
import { GENRES, METERS, barBeats, groupStarts, meterRhythm, placeGenre, type PlaceGenre } from './genres';
import {
  makeMotif, nearest, planForm, planHarmony, propose, selectPhrase, voiceLead,
  type Gesture, type MelodyNote, type Phrase4, type Synth4Options, type SynthScore4,
} from './compose4';

export const SYNTH_COMPOSER_VERSION_5 = 'synth-produced-5';
type LeadPatch = Pick<Patch, 'wave' | 'voices' | 'detune' | 'cutoff' | 'resonance' | 'attack' | 'decay' | 'sustain' | 'release' | 'envAmount' | 'vibrato' | 'glide' | 'send' | 'chorus'> & { describe: string };
/** Lead instruments to audition; null keeps v2's pulse lead. Cutoffs scale with the biome's brightness. */
export const LEAD_VOICES: Record<string, { label: string; patch: LeadPatch | null }> = {
  'soft-saw': { label: 'Soft saw (Jarre / Vangelis)', patch: { wave: 'sawtooth', voices: 2, detune: 7, cutoff: 2600, resonance: .8, attack: .025, decay: .5, sustain: .8, release: .4, envAmount: .8, vibrato: .0025, glide: .045, send: { reverb: .45, delay: .3 }, chorus: true, describe: '2× detuned saw, soft filter swell, chorus' } },
  'poly-brass': { label: 'Poly brass (80s synth)', patch: { wave: 'sawtooth', voices: 3, detune: 11, cutoff: 1500, resonance: 1.2, attack: .04, decay: .35, sustain: .75, release: .3, envAmount: 1.6, vibrato: .002, glide: .04, send: { reverb: .4, delay: .22 }, chorus: true, describe: '3× saw brass, filter bite on the attack' } },
  square: { label: 'Hollow square', patch: { wave: 'square', voices: 2, detune: 4, cutoff: 3000, resonance: .7, attack: .012, decay: .3, sustain: .8, release: .3, envAmount: .5, vibrato: .003, glide: .04, send: { reverb: .4, delay: .3 }, chorus: false, describe: '50% square, hollow and clear' } },
  glass: { label: 'Glass flute', patch: { wave: 'triangle', voices: 2, detune: 5, cutoff: 5000, resonance: .5, attack: .05, decay: .6, sustain: .85, release: .5, envAmount: 0, vibrato: .004, glide: .05, send: { reverb: .55, delay: .35 }, chorus: false, describe: 'soft triangle, flute-like' } },
  pulse: { label: 'Pulse (v2)', patch: null },
};
export type LeadVoiceId = keyof typeof LEAD_VOICES;
export interface Synth5Options extends Synth4Options { lead?: LeadVoiceId }
export type SynthScore5 = Omit<SynthScore4, 'composer'> & { composer: 5 };
const mod = (n: number, d: number) => ((n % d) + d) % d;
const clamp = (n: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, n));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
/** Instruments silenced by dropped beats and stop-time; pads, leads and tails ring through. */
const RHYTHM = new Set<InstId>(['kick', 'snare', 'clap', 'hat', 'ohat', 'shaker', 'tom', 'bass', 'stab', 'arp']);

export function composeSynth5(req: SynthRequest, options: Synth5Options = {}): SynthScore5 {
  // ── identity: exactly v4's setup and streams ─────────────────────────────
  const adventure = Number.isFinite(options.adventure) ? clamp(options.adventure!, 0, 1) : .55;
  // The place picks its genre unless the listener overrides it.
  const chosen: PlaceGenre = !options.genre || options.genre === 'place' ? placeGenre(req) : options.genre;
  const genre = chosen !== 'scene' ? GENRES[chosen] : undefined;
  const meterId = options.meter && options.meter !== 'genre' ? options.meter : genre?.meter ?? '4/4';
  const meter = METERS[meterId], length = barBeats({ meter }), rhythm = meterRhythm(meterId), accents = groupStarts(meterId);
  const stream = (layer: string) => new Rng(req.seed, `v4|${req.biome}|${req.scene}|${req.night ? 'n' : 'd'}|${layer}`);
  const prod = (layer: string) => new Rng(req.seed, `v5|${req.biome}|${req.scene}|${req.night ? 'n' : 'd'}|${layer}`);
  const key = new Rng(req.seed, 'world').int(0, 11), modeRng = new Rng(req.seed, `mode2|${req.biome}`);
  modeRng.chance(.5);
  const planetMode = modeRng.pick(PALETTES[req.biome].modes);
  // A fight keeps the planet's key but darkens a bright mode.
  const mode = req.scene === 'fight' && ['ionian', 'lydian', 'mixolydian', 'dorian'].includes(planetMode) ? 'harmonic minor' : planetMode, scale = new Scale(key, MODES[mode]);
  const palette = PALETTES[req.biome], archetype = ARCHETYPES[req.scene];
  const ambient = !genre && req.scene === 'exploration';
  const fight = req.scene === 'fight';
  const chill = !fight && (genre ? ['waltz', 'jig'].includes(genre.feel) : req.scene === 'small-city');
  const flight = !genre && req.scene === 'flight';
  const pulse = !ambient && (flight || fight || genre?.feel === 'electronic' || (!genre && req.scene === 'large-city'));
  const rng = stream('form'), plan = planForm(req, rng);
  // A genre keeps its feel in a fight but plays ~30% faster.
  const tempo = genre ? (fight ? [genre.tempo[0] * 1.3, Math.min(172, genre.tempo[1] * 1.3)] : genre.tempo) : archetype.tempo;
  const bpm = Math.round(rng.range(tempo[0], tempo[1]) * (req.night ? (fight ? .95 : .9) : 1));
  const swing = meterId !== '4/4' ? 0 : chill ? .1 : req.scene === 'large-city' ? .025 : 0;
  const motif = makeMotif(new Rng(req.seed, `v4|${req.biome}|identity`), meterId);
  const patches = makeV2Patches(req);
  const leadVoice = LEAD_VOICES[options.lead ?? 'pulse']?.patch;
  if (leadVoice) patches.lead = { ...patches.lead!, ...leadVoice, cutoff: leadVoice.cutoff * palette.bright * (req.night ? .75 : 1) };
  const grooveRng = stream('groove');
  const groove = ambient ? 'Free breathing' : meterId !== '4/4' ? METERS[meterId].label
    : genre?.feel === 'electronic' || fight ? 'Motor pulse' : grooveRng.pick(chill ? ['Broken pocket', 'Late backbeat', 'Tresillo'] : ['Motor pulse', 'Broken pocket', 'Tresillo']);
  const kicks = meterId !== '4/4' ? rhythm.kicks : groove === 'Motor pulse' ? [0, 1, 2, 3] : groove === 'Tresillo' ? [0, 1.5, 3] : groove === 'Late backbeat' ? [0, 2.5] : [0, 1.75, 2.5];
  const bars = plan.reduce((sum, p) => sum + p.bars, 0), beats = bars * length;
  const themeChords = planHarmony(scale, stream('theme-harmony'), 'theme', true, 0, key);
  const theme = propose(motif, 'original', scale, themeChords, stream('theme'), 0, meterId);
  const human = stream('performance');

  // ── performance material (v5 streams) ────────────────────────────────────
  const steps = Math.round(length * 4), eighths = Math.round(length * 2), lastGroup = accents[accents.length - 1];
  const onAccent = (at: number) => accents.some(a => Math.abs(a - at) < 1e-3);
  // ── battle ostinato: ONE grid shared by chug, bass, hats and arp, locked to the kick/snare ──
  // Each beat group splits into beats (a 2-beat group → two beats; a dotted 1.5 stays whole).
  const units: [number, number][] = accents.flatMap((g, i) => {
    const span = (accents[i + 1] ?? length) - g;
    return span >= 2 ? Array.from({ length: Math.round(span) }, (_, k) => [g + k, 1] as [number, number]) : [[g, span] as [number, number]];
  });
  /** Gallop (x.xx) on every beat, or plain 8ths; every beat is always hit, so nothing sits against the backbeat. */
  const gallop = (full: boolean) => units.flatMap(([g, span]) => (span >= 1.5 ? (full ? [0, .5, 1, 1.25] : [0, .5, 1]) : full ? [0, .5, .75] : [0, .5]).map(c => g + c));
  const onUnit = (at: number) => units.some(([g]) => Math.abs(g - at) < 1e-3);
  const pr = prod('rhythm');
  const arpLen = pr.pick([3, 5, 6, 7].filter(n => steps % n !== 0));
  const percK = pr.pick([3, 5]), percRot = pr.int(0, 7);
  const fillKind = pr.pick(['snare-roll', 'toms', 'stutter'] as const);
  const drums = prod('drums'), arr = prod('arrangement'), expr = prod('expression'), counter = prod('counter'), drops = prod('drops');
  const scalePcs = Array.from({ length: 7 }, (_, d) => mod(scale.pitch(d), 12));
  const stepBelow = (p: number) => { let q = p - 1; while (!scalePcs.includes(mod(q, 12))) q--; return q; };
  // ── consonance: a melody note may be a passing tone, but never a semitone
  // (or major 7th / minor 9th) against anything sounding with it ──
  const rubs = (p: number, pcs: number[]) => pcs.some(x => [1, 11].includes(mod(p - x, 12)));
  /** Nearest pitch (within a fourth) in one of `pools` that rubs nothing in `sounding`; at equal
   * distance an earlier pool (chord tones) wins, so the melody moves as little as possible. */
  const consonant = (p: number, pools: number[][], sounding: number[]) => {
    for (let d = 0; d <= 5; d++) for (const pool of pools) for (const q of d ? [p - d, p + d] : [p]) if (pool.includes(mod(q, 12)) && !rubs(q, sounding)) return q;
    return null;
  };
  const PITCHED = new Set<InstId>(['pad', 'stab', 'drone', 'bass', 'arp', 'keys', 'bell']);
  const melodyNotes = new Set<SNote>(), barRoots: number[] = [], exempt = new Set<SNote>();
  let prevSounding: number[] = [];
  const open = req.night ? 5200 : 16000;

  const notes: SNote[] = [], sections: SynthScore['sections'] = [], phrases: Phrase4[] = [], energy: number[] = [];
  const automation: { beat: number; tone: number }[] = [], breaks: { beat: number; beats: number }[] = [], silences: { beat: number; beats: number }[] = [];
  const add = (inst: InstId, pitch: number, at: number, len: number, velocity: number, extra: Partial<SNote> = {}) => {
    const offbeat = Math.abs(mod(at, 1) - .5) < .001;
    const timed = at + (offbeat && inst !== 'pad' ? swing : 0);
    if (timed >= beats || timed < 0) return null;
    const note: SNote = { inst, pitch, beat: timed, beats: Math.max(.04, Math.min(len, beats - timed)), vel: clamp(velocity, .1, 1), ...extra };
    notes.push(note);
    return note;
  };
  const addMelody = (...args: Parameters<typeof add>) => { const n = add(...args); if (n) melodyNotes.add(n); };
  let bar = 0, voicing: number[] = [], previous: MelodyNote[] = [], previousRoot = key, prevLead = 0;
  for (let si = 0; si < plan.length; si++) {
    const section = plan[si], next = plan[si + 1], startBar = bar, startNote = notes.length;
    const sparse = ambient || ['invitation', 'space', 'coda'].includes(section.role);
    const building = !!next && next.energy - section.energy > .2;
    for (let local = 0; local < section.bars; local += 4) {
      // Phrase selection: identical to v4.
      const firstTheme = section.role === 'theme' && local === 0;
      const recall = section.role === 'return' && local === 0;
      const home = section.role === 'coda' || section.role === 'return' || local + 4 === section.bars;
      const restore = firstTheme || recall;
      const phraseRng = stream(`phrase:${bar}`);
      const planned = restore ? themeChords : planHarmony(scale, phraseRng, section.role, home, adventure, previousRoot);
      // Battle harmony: the borrowed colour becomes the ♭II (Neapolitan) major — danger, not wistfulness.
      const chords = fight ? planned.map(c => {
        if (!c.borrowed) return c;
        const root = mod(key + 1, 12);
        return { ...c, root, pcs: [0, 4, 7].map(d => mod(root + d, 12)), name: `${KEYS[root]}*` };
      }) : planned;
      const target = clamp(section.novelty * (.4 + adventure * 1.1), 0, 1);
      const selected = restore ? { melody: theme.map(n => ({ ...n })), gesture: (recall ? 'recall' : 'original') as Gesture, novelty: 0 }
        : selectPhrase(motif, theme, previous, section.role, target, scale, chords, phraseRng, meterId);
      const drone = sparse && !ambient && section.role !== 'coda' ? [mod(key, 12), mod(key + 7, 12)] : [];
      const barOf = (x: number) => clamp(Math.floor(x / length), 0, 3);
      const triad = (b: number) => chords[b].pcs.slice(0, 3);
      // A fight's theme sections grind a tonic pedal under the moving chords (where the chord allows it).
      const pedal = [0, 1, 2, 3].map(b => fight && (section.role === 'theme' || section.role === 'answer') && !rubs(key, triad(b)));
      const held = (b: number) => [...drone, ...(pedal[b] ? [mod(key, 12)] : [])];
      // Rhythm: v4's expansion stretches time ×1.7 (onsets at .85, 1.7, 2.55…); snap to the 16th grid —
      // to 8ths in a fight, so the hook rides the ostinato instead of cutting across it.
      const grid = fight ? 2 : 4;
      const snapped = selected.melody.map(n => ({ ...n, at: Math.round(n.at * grid) / grid })).filter((n, i, a) => i === 0 || n.at > a[i - 1].at);
      // Pitch: a note that rubs its chord (or the next one, if it rings into it) moves to the nearest
      // chord tone, else scale tone, that doesn't; failing both it ends at the barline instead.
      const melody = snapped.map((n, i) => {
        let len = Math.max(.18, Math.min(n.length, (snapped[i + 1]?.at ?? 4 * length - .3) - n.at - .1)), pitch = n.pitch;
        if (fight && i < snapped.length - 1) len = Math.min(len, .9); // punchy: battle hooks are short and hard
        const b0 = barOf(n.at), b1 = barOf(n.at + len - .05), sounding = [...new Set([...triad(b0), ...triad(b1), ...held(b0), ...held(b1)])];
        if (rubs(pitch, sounding)) {
          const q = consonant(pitch, [triad(b0), scalePcs], sounding);
          if (q !== null) pitch = q;
          else { pitch = consonant(pitch, [triad(b0)], [...triad(b0), ...held(b0)]) ?? pitch; len = Math.max(.18, (b0 + 1) * length - n.at - .08); }
        }
        return { ...n, pitch, length: len };
      });
      // The 7th colours the harmony only in bars where it rubs no melody note (a maj7 against a
      // melody on the root is the classic semitone grind).
      const allowed = [0, 1, 2, 3].map(b => melody.some(n => n.at < (b + 1) * length && n.at + n.length > b * length && rubs(n.pitch, [chords[b].pcs[3]]))
        ? triad(b) : chords[b].pcs);
      phrases.push({ bar, bars: 4, section: section.label, role: section.role, gesture: selected.gesture,
        sourceBar: firstTheme ? null : 4, novelty: selected.novelty, targetNovelty: target,
        cadence: home || restore ? 'home' : 'open', chords: chords.map(c => c.name), borrowed: chords.some(c => c.borrowed) });
      const melodyInst: InstId = ['invitation', 'space', 'coda'].includes(section.role) ? (ambient ? 'bell' : 'keys') : 'lead';
      const lead = melodyInst === 'lead';

      // Per-bar energy: ramps over the section's last three bars toward the next.
      const barE = [0, 1, 2, 3].map(b => {
        const ramp = clamp((local + b - (section.bars - 3)) / 3);
        return clamp(lerp(section.energy, next?.energy ?? section.energy * .5, ramp * .6));
      });
      const doubled = lead && section.role === 'return';
      // Pickup into the returning theme: a 16th before the bar, on a scale step that agrees with the chord it sounds over.
      if (recall && lead) {
        let p = stepBelow(melody[0].pitch);
        for (let k = 0; k < 3 && rubs(p, prevSounding); k++) p = stepBelow(p);
        if (!rubs(p, prevSounding)) addMelody('lead', p, bar * length - .25, .22, .62, { bright: .9 });
      }
      melody.forEach((n, i) => {
        const e = barE[Math.min(3, Math.floor(n.at / length))], last = i === melody.length - 1, pitch = n.pitch;
        // Portamento only on small steps into held notes: a legato connection, never a swoop.
        const glide = lead && !fight && !doubled && prevLead > 0 && Math.abs(pitch - prevLead) >= 1 && Math.abs(pitch - prevLead) <= 2 && n.length >= .75 && expr.chance(.4);
        const v = .63 + e * .18 + human.range(-.045, .045), bright = (.45 + .75 * e) * (fight ? 1.15 : 1);
        addMelody(melodyInst, pitch, bar * length + n.at, n.length, v, { bright, throw: last && !ambient, glide });
        if (doubled && pitch + 12 <= 88) addMelody('lead', pitch + 12, bar * length + n.at, n.length, v * .35, { bright: bright * .8 });
        if (lead) prevLead = pitch;
      });
      const occupied = (at: number, len = .5) => melody.some(n => at < n.at + n.length + .12 && at + len > n.at - .12);
      const compStyle = arr.pick(['plain', 'skip', 'anticipate', 'invert'] as const);
      const resting: InstId | null = (section.role === 'development' || section.role === 'answer') && local > 0 && arr.chance(.6)
        ? arr.pick(['arp', 'hat', 'keys'] as InstId[]) : null;
      const hatRot = drums.int(0, 3);

      for (let b = 0; b < 4; b++) {
        const lb = local + b, t = (bar + b) * length, chord = chords[b], e = barE[b];
        const sectionEnd = lb === section.bars - 1, phraseEnd = b === 3;
        const bright = clamp(.45 + .75 * e, .35, 1.25), vel = .55 + .5 * e;
        energy.push(e);
        voicing = voiceLead(chord, voicing);
        const root = 36 + mod(chord.root, 12), pcs = allowed[b], sounds = (p: number) => pcs.includes(mod(p, 12));
        barRoots[bar + b] = root;

        // ── master tone: quiet sections close down and reopen into the next ──
        const rise = building ? clamp((lb - (section.bars - 2) + 1) / 2) : 0;
        const toneAmount = ambient ? .45 + .55 * e : section.role === 'space' ? .2 + .8 * rise : sparse ? .6 + .4 * rise : 1;
        automation.push({ beat: t, tone: open * toneAmount });

        // ── harmony: pads, or chopped pulse chords once a city/flight track drives ──
        const stabbed = pulse && !sparse && (fight || e >= .45);
        if (!stabbed) for (const pitch of voicing.filter(sounds)) add('pad', pitch, t, length, .37 + e * .14, { bright: bright * .8, brightEnd: bright });
        else {
          const pump = e >= .6 && !fight;
          // A fight chugs the shared gallop: every beat struck, accents on the beats.
          const grid = fight ? gallop(e > .85)
            : meterId === '4/4' ? (pump ? [0, 1, 2, 3] : Array.from({ length: 16 }, (_, i) => i / 4).filter(x => !(x % 1 === .75 && arr.chance(.4))))
            : pump ? accents.flatMap(a => [a, a + .5]).filter(x => x < length) : Array.from({ length: eighths }, (_, i) => i / 2);
          // Chugs play the triad: a hammered 7th against the root is a semitone grind.
          for (const x of grid) for (const pitch of voicing.filter(p => triad(b).includes(mod(p, 12))))
            add('stab', pitch, t + x, pump ? .45 : fight ? .16 : meterId === '4/4' ? .18 : .3, (fight ? (onUnit(x) ? .95 : .6) : onAccent(x) || x % 1 === 0 ? .85 : .55) * vel, { bright });
        }
        if (sparse && !ambient && lb === 0 && section.role !== 'coda')
          for (const pitch of [36 + key, 43 + key]) add('drone', pitch, t, section.bars * length, .7);

        // ── keys comping: listens to the melody, varies per phrase ──
        if (lead && !stabbed && resting !== 'keys') {
          let replies = [...rhythm.replies];
          if (compStyle === 'skip' && replies.length > 1) replies = replies.filter((_, i) => i !== 1);
          if (compStyle === 'anticipate') replies = replies.map((x, i) => i === 0 && x >= .5 ? x - .5 : x);
          const tones = ambient ? [voicing[2] + 12] : compStyle === 'invert' ? [voicing[2], voicing[3], voicing[1] + 12] : [voicing[1], voicing[2], voicing[3]];
          for (const at of replies) {
            if (meterId !== '3/4' && occupied(b * length + at, .4)) continue;
            for (const pitch of tones.filter(sounds)) add(ambient ? 'bell' : 'keys', pitch, t + at, Math.min(.4, length - at - .12), meterId === '3/4' ? .34 : .44, { pan: -.22 });
          }
        }
        // ── bells: v4's replies in the melody's breath; a counter-line on the return ──
        if (!sparse && !fight && (b === 1 || b === 3)) for (const at of [length - 1, length - .5]) {
          if (occupied(b * length + at, .25)) continue;
          add('bell', nearest(scale.pitch(motif[motif.length - (at === length - 1 ? 2 : 1)].degree, 4), pcs, 60, 81), t + at, .3, .47, { pan: .28 });
        }
        if (section.role === 'return' && !fight && (b === 0 || b === 2) && e > .6) {
          const free = Array.from({ length: eighths }, (_, i) => i / 2).filter(x => !occupied(b * length + x, .5));
          for (let k = 0; k < Math.min(counter.int(1, 2), free.length); k++) {
            let pitch = 72 + pcs[(k + 1) % pcs.length];
            while (pitch > 84) pitch -= 12;
            const at = free[Math.floor(counter.next() * free.length)];
            add('bell', pitch, t + at, Math.min(1.2, length - at - .05), .42 + .2 * e, { pan: counter.range(-.5, .5), throw: k === 0 && phraseEnd });
          }
        }

        // ── arp: odd length, phasing against the bar ──
        const arpOn = resting !== 'arp' && (ambient ? e >= .4 && section.role !== 'coda' : (!sparse && e >= .5) || (pulse && section.role === 'return'));
        if (arpOn) {
          const rate = ambient || (chill && e < .6) ? .5 : .25;
          const pool = (e > .8 ? [0, 12, 24] : [0, 12]).flatMap(o => pcs.map(pc => 60 + pc + o)).sort((u, v) => v - u);
          for (let i = 0; i < Math.round(length / rate); i++) {
            if (ambient && arr.chance(.35)) continue;
            if (e < .55 && i % 2) continue;
            const cycle = fight ? 4 : arpLen; // a fight's arp repeats per beat: no phasing against the ostinato
            const step = Math.round((t + i * rate) / rate), run = Math.floor(step / cycle);
            add('arp', pool[(step % cycle + (fight ? 0 : run % 2)) % pool.length], t + i * rate, rate * .7, (onAccent(i * rate) ? .9 : .62) * vel, { pan: i % 2 ? .3 : -.3, bright });
          }
        }

        // ── bass: held → kick-locked → syncopated as energy rises ──
        if (sparse && fight) for (let i = 0; i < eighths; i++) add('bass', root, t + i / 2, .4, i % 2 ? .5 : .65, { bright: .7 }); // tension: a low 8th pulse
        else if (sparse) add('bass', root, t, length - .15, .55);
        else if (fight) {
          // The riff: bass doubles the chug's gallop — tonic pedal where allowed, an octave kick on every other beat.
          const low = pedal[b] ? 36 + mod(key, 12) : root;
          units.forEach(([g, span], u) => {
            const cells = (span >= 1.5 ? (e > .85 ? [0, .5, 1, 1.25] : [0, .5, 1]) : e > .85 ? [0, .5, .75] : [0, .5]);
            cells.forEach((c, k) => add('bass', low + (u % 2 === 1 && k === cells.length - 1 && e > .7 ? 12 : 0), t + g + c, .2, (k === 0 ? .95 : .72) * vel, { bright }));
          });
        } else if (flight && e >= .5) {
          const r = meterId === '4/4' ? .25 : .5;
          for (let i = 0; i < Math.round(length / r); i++)
            add('bass', root + (i % 8 === 7 && e > .7 ? 12 : 0), t + i * r, r * .8, (onAccent(i * r) ? .95 : .7) * vel, { bright });
        } else if (e < .5) {
          let nextRoot = 36 + mod(chords[(b + 1) % 4].root, 12);
          while (nextRoot - root > 6) nextRoot -= 12; while (root - nextRoot > 6) nextRoot += 12;
          const approach = nextRoot === root ? root + 7 : nextRoot + (nextRoot > root ? -1 : 1);
          const approachAt = meterId === '4/4' ? 3 : length - .5, fifthAt = lastGroup > 0 && lastGroup < approachAt ? lastGroup : approachAt;
          add('bass', root, t, fifthAt - .1, .9 * vel, { bright });
          if (fifthAt < approachAt) add('bass', root + 7 > 52 ? root - 5 : root + 7, t + fifthAt, approachAt - fifthAt - .1, .8 * vel, { bright });
          add('bass', approach, t + approachAt, length - approachAt - .1, .75 * vel, { bright });
        } else if (e < .8) {
          const bassRhythm = [...new Set([0, ...kicks.slice(1), ...(e > .7 && meterId !== '3/4' ? [length - .5] : [])])].sort((x, y) => x - y);
          bassRhythm.forEach((at, i) => {
            const approach = i === bassRhythm.length - 1 && at >= length - .5 && b < 3;
            const nextRoot = chords[(b + 1) % 4].root;
            const pitch = approach ? nearest(root, [mod(nextRoot - 1, 12), mod(nextRoot + 1, 12)], 33, 50) : root + (i === 1 && groove === 'Motor pulse' ? 12 : 0);
            add('bass', pitch, t + at, Math.min(.85, (bassRhythm[i + 1] ?? length) - at - .12), (.74 + (i === 0 ? .13 : 0)) * (.8 + .3 * e), { bright });
          });
        } else {
          euclid(Math.round(steps * .69), steps, 0).forEach((on, i) => {
            if (on) add('bass', root + (i % 4 === 2 ? 12 : 0), t + i / 4, .22, (onAccent(i / 4) ? .95 : .72) * vel, { bright });
          });
        }

        // ── drums ──
        const drumsOn = !sparse;
        const hatsOn = !ambient && resting !== 'hat' && (drumsOn ? e >= .35 : building && lb >= section.bars - 2);
        if (drumsOn) {
          for (const at of kicks) add('kick', 36, t + at, .2, at === 0 ? .9 : .7);
          if (fight && e > .85) for (const at of [lastGroup - .25, length - .25]) if (at > 0 && !kicks.includes(at)) add('kick', 36, t + at, .15, .62); // double-kick pickups
          const snares = meterId === '4/4' && (req.night || groove === 'Late backbeat') ? [2] : rhythm.snares;
          for (const at of snares) {
            const at2 = at + (chill && meterId === '4/4' ? .018 : 0), v = (meterId === '3/4' ? .35 : .7) * (.8 + .3 * e);
            add('snare', 38, t + at2, .18, v);
            if ((e > .75 || fight) && !chill) add('clap', 38, t + at2 + .005, .3, v * .8);
          }
          if (e > .7 && !req.night) { const at = meterId === '4/4' ? (flight ? 2.5 : 3.5) : lastGroup + .5; if (at < length) add('ohat', 46, t + at, .5, .5); }
          if (e > .5 && !fight) euclid(percK, eighths, percRot).forEach((on, i) => { if (on) add('shaker', 70, t + i / 2 + .25, .1, .32); });
        }
        if (hatsOn && fight) {
          // Straight hats on the shared grid: 16ths at the peak, 8ths otherwise, accents on the beats.
          for (let x = 0; x < length - 1e-6; x += e > .85 ? .25 : .5) add('hat', 42, t + x, .06, (onUnit(x) ? .6 : .4) * (.8 + .3 * e), { pan: -.12 });
        } else if (hatsOn) {
          const density = Math.min(.85, (e > .8 ? .75 : e > .55 ? .5 : .32) * (req.night && !fight ? .65 : 1) * (fight ? 1.15 : 1));
          euclid(Math.max(2, Math.round(steps * density)), steps, hatRot).forEach((on, i) => {
            if (!on) return;
            const at = i / 4, accent = meterId === '4/4' ? i % 4 === 2 : onAccent(at);
            add(chill ? 'shaker' : 'hat', 42, t + at, .08, (accent ? .55 : .36) * (.75 + .4 * e) + drums.range(-.03, .03), { pan: -.12 });
          });
        }

        // ── battle: war drums keep the pulse alive when the band drops out ──
        if (fight && sparse && section.role !== 'coda') {
          const build = (lb + 1) / section.bars;
          for (const [g] of units) for (const c of [0, .75]) if (g + c < length - 1e-6) add('tom', c ? 45 : 41, t + g + c, .3, .45 + .4 * build);
          if (section.role === 'space') { add('kick', 36, t, .2, .8); add('snare', 38, t + (lastGroup || length / 2), .2, .6); } // half-time
          if (section.role === 'invitation' && lb === 0) { add('crash', 49, t, 2, .85); add('kick', 36, t, .2, 1); } // the opening rupture
          if (lb === section.bars - 1) for (let x = length / 2; x < length - 1e-6; x += .25) add('snare', 38, t + x, .1, .4 + .5 * (x - length / 2) / (length / 2));
        }

        // ── transitions ──
        let silenced = false;
        if (sectionEnd && next && !ambient) {
          const intoBig = next.energy - e > .2 || next.role === 'return' || next.role === 'theme';
          if (drumsOn && intoBig && drops.chance(.7)) { const from = Math.max(lastGroup, length - 1); silences.push({ beat: t + from, beats: length - from }); silenced = true; }
          if (building) add('riser', 60, t - length >= 0 ? t - length : t, t - length >= 0 ? 2 * length : length, .5 + .3 * next.energy);
        }
        if (fight && drumsOn && phraseEnd && !sectionEnd && !silenced && e > .8 && drops.chance(.5)) {
          // Unison hits: the whole band strikes together on the last beat group, then drops out.
          silences.push({ beat: t + lastGroup, beats: length - lastGroup }); silenced = true;
          const hits = [...new Set([lastGroup, lastGroup + .5, length - .5])].filter(x => x < length - 1e-6);
          hits.forEach((x, i) => {
            for (const n of [
              ...voicing.filter(p => triad(b).includes(mod(p, 12))).map(p => add('stab', p, t + x, .2, .95 * vel, { bright })),
              add('kick', 36, t + x, .2, .95), add('clap', 38, t + x, .2, .8), add('bass', root, t + x, .22, .95 * vel, { bright }),
              ...(i === 0 ? [add('crash', 49, t + x, 1.5, .7)] : []),
            ]) if (n) exempt.add(n);
          });
        }
        if (drumsOn && phraseEnd && !sectionEnd && !silenced && e >= .4 && drops.chance(pulse ? .4 : .2)) {
          breaks.push({ beat: t + length - .5, beats: .5 }); silences.push({ beat: t + length - .5, beats: .5 }); silenced = true;
        }
        if (drumsOn && phraseEnd && !silenced && (sectionEnd || drums.chance(fight ? .75 : .4))) {
          const intoBig = sectionEnd && !!next && next.energy > section.energy;
          const kind = intoBig ? fillKind : drums.pick(['snare-roll', 'toms', 'stutter'] as const);
          const from = meterId === '4/4' ? (intoBig ? 2 : 3) : lastGroup;
          for (let x = from; x < length - 1e-6; x += kind === 'stutter' ? .125 : .25) {
            const v = .45 + .5 * (x - from) / (length - from);
            if (kind === 'toms') add('tom', Math.max(40, 52 - Math.floor((x - from) * 4) * 2), t + x, .3, v);
            else add(kind === 'stutter' ? 'hat' : 'snare', kind === 'stutter' ? 42 : 38, t + x, .1, v);
          }
        }
        if ((lb === 0 || (fight && b === 0 && e > .85)) && !ambient && ['theme', 'answer', 'return', 'development'].includes(section.role)) add('crash', 49, t, 2, .8);
      }
      previous = selected.melody; previousRoot = chords[3].root; prevSounding = [...allowed[3], ...drone]; bar += 4;
    }
    if (fight && section.role === 'return') {
      // Escalation: the last stand lifts a whole step (the pickup into it stays in the old key).
      for (let i = startNote; i < notes.length; i++) if (notes[i].beat >= startBar * length - 1e-6 && (PITCHED.has(notes[i].inst) || notes[i].inst === 'lead')) notes[i].pitch += 2;
      for (let k = startBar; k < bar; k++) barRoots[k] += 2;
    }
    sections.push({ label: section.label, bar: startBar, bars: section.bars, layers: [...new Set(notes.slice(startNote).map(n => n.inst))] });
  }

  // Safety net: nothing pitched may rub a sounding melody note. A bass note (chromatic
  // approach, a fifth over an altered chord) moves to its bar's root; anything else yields.
  const buckets = new Map<number, SNote[]>();
  for (const m of melodyNotes) for (let k = Math.floor(m.beat / length); k <= Math.floor((m.beat + m.beats) / length); k++) (buckets.get(k) ?? buckets.set(k, []).get(k)!).push(m);
  const rubsMelody = (n: SNote, pitch = n.pitch) => {
    for (let k = Math.floor(n.beat / length); k <= Math.floor((n.beat + n.beats) / length); k++) for (const m of buckets.get(k) ?? [])
      if (Math.min(m.beat + m.beats, n.beat + n.beats) - Math.max(m.beat, n.beat) > .03 && rubs(pitch, [m.pitch])) return true;
    return false;
  };
  const yielded = new Set<SNote>();
  for (const n of notes) {
    if (melodyNotes.has(n) || !PITCHED.has(n.inst) || !rubsMelody(n)) continue;
    if (n.inst === 'bass') {
      let p = barRoots[clamp(Math.floor(n.beat / length), 0, barRoots.length - 1)];
      while (p - n.pitch > 6) p -= 12; while (n.pitch - p > 6) p += 12;
      if (!rubsMelody(n, p)) { n.pitch = p; continue; }
    }
    yielded.add(n);
  }
  const clean = notes.filter(n => !yielded.has(n) && !(RHYTHM.has(n.inst) && !exempt.has(n) && silences.some(s => n.beat >= s.beat - 1e-6 && n.beat < s.beat + s.beats)))
    .sort((a, b) => a.beat - b.beat || a.pitch - b.pitch);
  const used = new Set(clean.map(n => n.inst));
  for (const id of Object.keys(patches) as InstId[]) if (!used.has(id)) delete patches[id];
  const words = stream('title'), title = `${words.pick(palette.words)} ${words.pick(NOUNS[req.scene])}`;
  const fxr = prod('fx');
  const fx: FxParams = {
    // A fight is tight and dirty: a shorter room, more drive, harder pumping.
    reverbSeconds: palette.reverb * (req.night ? 1.25 : 1) * (ambient ? 1.3 : fight ? .6 : 1), reverbTone: fxr.range(3500, 7000) * palette.bright,
    delayBeats: fight ? (fxr.pick([.75, .75, .5, 1.5]), .5) : fxr.pick([.75, .75, .5, 1.5]), delayFeedback: palette.delayFb * (fight ? .6 : 1), drive: fight ? Math.max(palette.drive, .4) : palette.drive,
    tone: open, duckDepth: ambient ? 0 : chill ? .25 : fight ? .55 : .5, width: .55, master: fight ? .4 : .45, // unison hits stack: ~1 dB more headroom
  };
  return {
    version: SYNTH_COMPOSER_VERSION_5, composer: 5, request: { ...req }, title,
    style: (genre ? (fight ? `${genre.label} battle` : genre.label) : archetype.style) + (req.night ? ' (night)' : ''),
    meter: { numerator: meter.numerator, denominator: meter.denominator, grouping: [...meter.grouping] }, genre: chosen,
    description: fight ? `Battle: one locked gallop ostinato (chug, bass, hats), a tonic pedal grinding under the chords, the hook on the 8th grid, a ♭II in the desperation, unison hits at phrase ends, war drums in the standoff and breath, and a last stand lifted a whole step. ${groove}, ${bpm} BPM.`
      : `v4's theme and form, performed like v2: ${groove.toLowerCase()}, energy ramps, ${arpLen}-step phasing arp, Euclidean hats on the ${steps}-step bar, ${LEAD_VOICES[options.lead ?? 'pulse']?.label.toLowerCase() ?? 'pulse'} lead, fills on the last beat group, a lifted return. Adventure ${Math.round(adventure * 100)}%.`,
    bpm, key, keyName: KEYS[key], mode: mode[0].toUpperCase() + mode.slice(1), swing, bars, beats, duration: beats * 60 / bpm,
    progression: [...new Set(phrases.filter(p => p.role === 'theme' || p.role === 'development').map(p => `${p.role === 'theme' ? 'theme' : 'detour'} ${p.chords.join('–')}`))],
    sections, patches, notes: clean, energy, automation, breaks, fx,
    mix: fight ? { bell: -4, bass: 3.5, kick: 2, lead: 1.5, arp: -4, stab: -1, keys: -3, hat: -8, ohat: -9, shaker: -7, snare: -3, clap: -3, crash: -6, riser: -3, tom: -2 }
      : { bell: -2, bass: 3, kick: 1, lead: 1.5, arp: -3, stab: -2, keys: -2, hat: -9, ohat: -9, shaker: -6, snare: -5, clap: -5, crash: -7, riser: -4 },
    development: { adventure, motif, groove, phrases },
  };
}
